import * as React from 'react';
import {
  FieldSchemaInfo,
  Metadata,
  SchemaField
} from '../../../../types/Metadata';
import { NodeType } from '../../../../enums/NodeType';
import {
  PrimaryAudience,
  Relationship,
  PrimaryAuthor,
  OtherEmployee,
  GenericField,
  CoAuthor,
  Calendar,
  AdditionalAudience,
  FieldIcon,
  URLField
} from '../../../Icon/icon';
import { DataType } from '../../../../enums/DataType';
import {
  cn,
  formatDate,
  getConfPresentationLink,
  getItemStatusId,
  getItemType
} from '../../../../utils';
import { useSelector } from '../../../../utils/useSelector';
import { ItemType } from '../../../../enums/ItemType';

type FieldProps = {
  label?: string;
  labels?: NodeType[];
  value?: string;
  values?: string[];
  children?: any;
  title?: string;
  fieldInfo?: FieldSchemaInfo;
};

const ICONS = {
  [DataType.date]: <Calendar />,
  [NodeType.Employee]: <OtherEmployee />,
  [DataType.number]: <FieldIcon.Numeric />,
  [DataType.longString]: <FieldIcon.Text />,
  [DataType.string]: <FieldIcon.Text />,
  [DataType.link]: <URLField />,
  ['dateByMonth']: <Calendar />,
  ['expiryDateByMonth']: <Calendar />,
  ['expiryDateByQuarter']: <Calendar />,
  ['dateByQuarter']: <Calendar />,
  ['author']: <PrimaryAuthor />,
  ['coAuthors']: <CoAuthor />,
  ['relationship']: <Relationship />,
  ['additionalRoles']: <AdditionalAudience />,
  ['additionalFunctions']: <AdditionalAudience />,
  ['additionalKeyInitiatives']: <AdditionalAudience />,
  ['additionalCoreTopics']: <AdditionalAudience />,
  ['additionalClientQuestions']: <AdditionalAudience />,
  ['role']: <PrimaryAudience />,
  ['function']: <PrimaryAudience />,
  ['keyInitiative']: <PrimaryAudience />,
  ['coreTopic']: <PrimaryAudience />,
  ['clientQuestion']: <PrimaryAudience />,
  ['PN Frame: Priorities Navigator']: <AdditionalAudience />,
  ['PN: Role']: <AdditionalAudience />,
  ['PN: Function']: <AdditionalAudience />,
  ['PN Frame: Key Initiative']: <AdditionalAudience />,
  ['PN Tile: Core Topic']: <AdditionalAudience />
};

const getReferenceFieldIcon = (
  field?: string,
  dataType?: NodeType | DataType,
  parentField?,
  displayName?: string
) => {
  return (
    ICONS[displayName] ||
    ICONS[field] ||
    ICONS[dataType] ||
    ICONS[parentField] || <GenericField />
  );
};

export const Field = ({
  label,
  labels,
  value,
  values,
  children,
  title,
  fieldInfo
}: FieldProps) => {
  const { schemaField, field, schemaType } = fieldInfo;
  let { name, displayName, dataType, parentField } = schemaField;

  let FC =
    dataType == DataType.boolean || field == 'daysInCurrentStatus'
      ? null
      : getReferenceFieldIcon(field, dataType, parentField, displayName);
  const cardSchemaType = useSelector(s => s.view?.selected?.card?.props?.data);

  let hasIcon = (labels && !label) || !!FC;
  let hasLabel = !labels && label;

  if (!!children && hasIcon) {
    return (
      <div className="item-card-field">
        <label>
          <span title={schemaType != cardSchemaType ? displayName : name}>
            {FC}
          </span>
        </label>
        {children}
      </div>
    );
  }
  if (!!children && !hasIcon) {
    return <div className="item-card-field">{children}</div>;
  }
  if (!values) {
    if (!value) {
      switch (typeof value) {
        case 'boolean':
        case 'number':
          break;
        default:
          return null;
      }
    }
    values = [value];
  }
  return (
    <div
      title={title}
      className={cn(
        'item-card-field',
        { ['with-title']: !!title },
        { ['with-label']: !hasIcon }
      )}
    >
      {hasIcon && !hasLabel && (
        <label>
          <span title={schemaType != cardSchemaType ? displayName : name}>
            {FC}
          </span>
        </label>
      )}
      {!hasIcon && <label>{label}:&nbsp;</label>}

      {hasIcon && hasLabel && (
        <>
          <label>
            <span title={schemaType != cardSchemaType ? displayName : name}>
              {FC}
            </span>
          </label>
          <span>
            <span style={{ fontWeight: '500' }}>{label}:&nbsp;</span>
            {values?.sort().join(', ') || ''}
          </span>
        </>
      )}
      {!(hasIcon && hasLabel) && <span>{values?.sort().join(', ') || ''}</span>}
    </div>
  );
};

export const getValue = (
  m: Metadata,
  sf: string | SchemaField,
  schemaField?: SchemaField
) => {
  if (!m.props) return null;
  let field: string = null;
  if (typeof sf == 'string') field = sf;
  else field = sf?.field;
  if (!field) return null;
  const itemType = getItemType(m);
  if (field == 'presentationLink' && itemType == ItemType.conferenceSession) {
    return getConfPresentationLink(m);
  }
  if (field == 'gcomURL' && !!m?.props?.itemId) {
    const statusId = getItemStatusId(m);
    if (statusId >= 800 || (statusId == 8 && itemType == ItemType.content)) {
      return process.env.GCOM_URL + m.props.itemId;
    }
    return null;
  }
  let val = m?.props[field];
  if (schemaField?.dataType == DataType.date) {
    val = formatDate(val);
  }
  if (schemaField?.dataType == DataType.enum) {
    const enumValues = schemaField.enumValues;
    if (enumValues && enumValues[val]) return enumValues[val];
  }
  return val;
};
